import React from "react";
import * as Icons from "lucide-react";
import { initials, avatarColor } from "@/lib/utils2";

// Styles de badge selon le statut du compte.
const STATUS_STYLE = {
  active: "bg-success/15 text-success",
  invited: "bg-warning/15 text-warning",
  disabled: "bg-destructive/15 text-destructive",
  pending_email: "bg-warning/15 text-warning",
  pending_approval: "bg-majorelle/15 text-majorelle",
  rejected: "bg-destructive/15 text-destructive",
};

// Section « Inscriptions en attente » (président) : valider / refuser les inscriptions libres.
export function RegistrationsSection({ registrations, onApprove, onReject, t }) {
  const pending = registrations.filter((r) => r.status === "pending_approval" || r.status === "pending_email");
  const rejected = registrations.filter((r) => r.status === "rejected");
  return (
    <div data-testid="registrations-section">
      <h2 className="font-bold mb-2 flex items-center gap-2 text-majorelle">
        <Icons.UserPlus className="h-4 w-4" /> {t("pending_registrations")} ({pending.length})
      </h2>
      {pending.length === 0 && <p className="text-sm text-muted-foreground mb-2">{t("no_pending_registrations")}</p>}
      <div className="space-y-2">
        {pending.map((r) => <RegistrationCard key={r.id} r={r} onApprove={onApprove} onReject={onReject} t={t} />)}
      </div>
      {rejected.length > 0 && (
        <details className="mt-3" data-testid="rejected-history">
          <summary className="text-sm text-muted-foreground cursor-pointer select-none">{t("rejected_history")} ({rejected.length})</summary>
          <div className="space-y-2 mt-2">
            {rejected.map((r) => (
              <div key={r.id} className="rounded-xl bg-secondary/40 p-3 text-sm flex items-center justify-between" data-testid={`reg-rejected-${r.id}`}>
                <span className="truncate">{r.name} · @{r.identifier}</span>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE.rejected}`}>{t("status_rejected")}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function RegistrationCard({ r, onApprove, onReject, t }) {
  const emailPending = r.status === "pending_email";
  return (
    <div className="rounded-2xl bg-majorelle/5 border border-majorelle/30 p-4" data-testid={`registration-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: avatarColor(r.name) }}>{initials(r.name)}</div>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{r.name}</p>
          <p className="text-xs text-muted-foreground truncate">@{r.identifier} · {t("apt")} {r.apartment || "—"}</p>
          <p className="text-xs text-muted-foreground truncate">{r.email} · {r.phone}</p>
        </div>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${STATUS_STYLE[r.status]}`}>{t("status_" + r.status)}</span>
      </div>
      {emailPending ? (
        <p className="text-xs text-warning mt-3 flex items-center gap-1.5"><Icons.Clock className="h-3.5 w-3.5" /> {t("awaiting_email_confirm")}</p>
      ) : (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onApprove(r)} data-testid={`reg-approve-${r.id}`} className="flex-1 h-10 rounded-xl bg-brand text-white font-semibold text-sm flex items-center justify-center gap-1.5"><Icons.Check className="h-4 w-4" /> {t("approve")}</button>
          <button onClick={() => onReject(r)} data-testid={`reg-reject-${r.id}`} className="flex-1 h-10 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm flex items-center justify-center gap-1.5"><Icons.X className="h-4 w-4" /> {t("reject")}</button>
        </div>
      )}
    </div>
  );
}

export function RoleRequestsSection({ requests, currentUserId, onDecide, t }) {
  return (
    <div data-testid="role-requests-section">
      <h2 className="font-bold mb-2 flex items-center gap-2 text-majorelle"><Icons.Users className="h-4 w-4" /> {t("role_requests")} ({requests.length})</h2>
      <div className="space-y-2">
        {requests.map((r) => (
          <RequestCard key={r.id} r={r} currentUserId={currentUserId} onDecide={onDecide} t={t} />
        ))}
      </div>
    </div>
  );
}

export function UsersList({ users, currentUserId, t, onWhatsApp, onRegen, onEdit, onResetPin, onToggle }) {
  return (
    <div className="space-y-2">
      {users.map((u) => (
        <UserRow key={u.id} u={u} currentUserId={currentUserId} t={t}
          onWhatsApp={onWhatsApp} onRegen={onRegen} onEdit={onEdit} onResetPin={onResetPin} onToggle={onToggle} />
      ))}
    </div>
  );
}

function RequestCard({ r, currentUserId, onDecide, t }) {
  const mine = r.approver_id === currentUserId;
  return (
    <div className="rounded-2xl bg-majorelle/5 border border-majorelle/30 p-4" data-testid={`role-request-${r.id}`}>
      <p className="font-bold">{r.target_name}</p>
      <p className="text-sm text-muted-foreground">{t("role_" + r.old_role)} → <span className="font-semibold text-foreground">{t("role_" + r.new_role)}</span></p>
      <p className="text-xs text-muted-foreground mt-0.5">{t("requested_by")}: {r.requested_by}</p>
      {mine ? (
        <div className="flex gap-2 mt-3">
          <button onClick={() => onDecide(r.id, true)} data-testid={`role-approve-${r.id}`} className="flex-1 h-10 rounded-xl bg-brand text-white font-semibold text-sm">{t("approve")}</button>
          <button onClick={() => onDecide(r.id, false)} data-testid={`role-reject-${r.id}`} className="flex-1 h-10 rounded-xl bg-secondary text-secondary-foreground font-semibold text-sm">{t("reject")}</button>
        </div>
      ) : (
        <p className="text-xs text-warning mt-2">{t("awaiting_treasurer")}: {r.approver_name}</p>
      )}
    </div>
  );
}

// ------- Ligne : un compte utilisateur -------
function UserRow({ u, currentUserId, t, onWhatsApp, onRegen, onEdit, onResetPin, onToggle }) {
  const invited = u.status === "invited";
  return (
    <div className="rounded-2xl bg-card border border-border p-4" data-testid={`user-${u.identifier}`}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full flex items-center justify-center text-white font-bold shrink-0" style={{ backgroundColor: avatarColor(u.name) }}>{initials(u.name)}</div>
        <div className="flex-1 min-w-0">
          <p className="font-bold truncate">{u.name}</p>
          <p className="text-xs text-muted-foreground">@{u.identifier} · {t("apt")} {u.apartment || "—"}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-[11px] font-bold bg-brand/10 text-brand dark:text-primary px-2 py-0.5 rounded-full">{t("role_" + u.role)}</span>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[u.status]}`}>{t("status_" + u.status)}</span>
        </div>
      </div>

      {invited && u.invite_code && (
        <div className="mt-3 rounded-xl bg-warning/10 px-3 py-2 text-sm">
          {t("invite_code")}: <span className="font-mono font-bold tracking-widest">{u.invite_code}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        {invited && (
          <button onClick={() => onWhatsApp(u, "invite")} data-testid={`wa-invite-${u.identifier}`}
            className="h-9 px-3 rounded-lg bg-[#25D366] text-white font-semibold text-sm flex items-center gap-1.5"><Icons.Send className="h-4 w-4" /> {t("send_whatsapp")}</button>
        )}
        {invited && (
          <button onClick={() => onRegen(u)} data-testid={`regen-${u.identifier}`} className="h-9 px-3 rounded-lg bg-secondary text-secondary-foreground font-semibold text-sm flex items-center gap-1.5"><Icons.RefreshCw className="h-4 w-4" /> {t("regenerate_code")}</button>
        )}
        <button onClick={() => onEdit(u)} data-testid={`edit-${u.identifier}`} className="h-9 px-3 rounded-lg bg-secondary text-secondary-foreground font-semibold text-sm flex items-center gap-1.5"><Icons.Pencil className="h-4 w-4" /> {t("edit")}</button>
        {u.status === "active" && (
          <button onClick={() => onResetPin(u)} data-testid={`reset-pin-${u.identifier}`} className="h-9 px-3 rounded-lg bg-secondary text-secondary-foreground font-semibold text-sm flex items-center gap-1.5"><Icons.KeyRound className="h-4 w-4" /> {t("reset_pin")}</button>
        )}
        {u.id !== currentUserId && (
          <button onClick={() => onToggle(u)} data-testid={`toggle-${u.identifier}`}
            className={`h-9 px-3 rounded-lg font-semibold text-sm flex items-center gap-1.5 ${u.status === "disabled" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
            <Icons.Power className="h-4 w-4" /> {u.status === "disabled" ? t("enable") : t("disable")}
          </button>
        )}
      </div>
    </div>
  );
}

